/**
 * API Monitor Utility
 * Helps track API calls and cache performance to debug 429 errors
 */

class ApiMonitor {
  constructor() {
    this.requests = [];
    this.cacheStats = new Map();
    this.isEnabled = true;
  }

  /**
   * Log an API request
   */
  logRequest(endpoint, method = 'GET', cached = false, service = 'unknown') {
    if (!this.isEnabled) return;

    const timestamp = new Date().toISOString();
    const request = {
      timestamp,
      endpoint,
      method,
      cached,
      service
    };

    this.requests.push(request);

    // Update cache stats
    const key = `${service}:${endpoint}`;
    if (!this.cacheStats.has(key)) {
      this.cacheStats.set(key, { total: 0, cached: 0, uncached: 0 });
    }

    const stats = this.cacheStats.get(key);
    stats.total++;
    if (cached) {
      stats.cached++;
    } else {
      stats.uncached++;
    }

    // Log to console for immediate debugging
    console.log(
      `🔍 API Monitor: ${cached ? '💾 CACHED' : '🌐 API'} ${method} ${endpoint} [${service}]`
    );

    // Keep only last 100 requests to prevent memory issues
    if (this.requests.length > 100) {
      this.requests = this.requests.slice(-100);
    }
  }

  /**
   * Get cache hit ratio for a service
   */
  getCacheHitRatio(service = null) {
    const filteredStats = service 
      ? Array.from(this.cacheStats.entries()).filter(([key]) => key.startsWith(service))
      : Array.from(this.cacheStats.entries());

    const totals = filteredStats.reduce(
      (acc, [, stats]) => ({
        total: acc.total + stats.total,
        cached: acc.cached + stats.cached,
        uncached: acc.uncached + stats.uncached
      }),
      { total: 0, cached: 0, uncached: 0 }
    );

    return {
      total: totals.total,
      cached: totals.cached,
      uncached: totals.uncached,
      hitRatio: totals.total > 0 ? (totals.cached / totals.total * 100).toFixed(1) : 0
    };
  }

  /**
   * Get recent requests (last N minutes)
   */
  getRecentRequests(minutes = 5) {
    const cutoff = new Date(Date.now() - minutes * 60 * 1000);
    return this.requests.filter(req => new Date(req.timestamp) > cutoff);
  }

  /**
   * Get frequent endpoints that might be causing issues
   */
  getFrequentEndpoints(minutes = 5) {
    const recent = this.getRecentRequests(minutes);
    const frequency = new Map();

    recent.forEach(req => {
      const key = `${req.endpoint} [${req.service}]`;
      frequency.set(key, (frequency.get(key) || 0) + 1);
    });

    return Array.from(frequency.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);
  }

  /**
   * Generate a comprehensive report
   */
  generateReport() {
    const recent = this.getRecentRequests(5);
    const overall = this.getCacheHitRatio();
    const frequent = this.getFrequentEndpoints(5);

    console.group('📊 API Monitor Report (Last 5 minutes)');
    
    console.log('📈 Overall Cache Performance:');
    console.log(`  Total Requests: ${overall.total}`);
    console.log(`  Cache Hits: ${overall.cached} (${overall.hitRatio}%)`);
    console.log(`  API Calls: ${overall.uncached}`);

    if (frequent.length > 0) {
      console.log('\n🔥 Most Frequent Endpoints:');
      frequent.forEach(([endpoint, count], index) => {
        console.log(`  ${index + 1}. ${endpoint}: ${count} calls`);
      });
    }

    if (recent.length > 0) {
      console.log('\n⏰ Recent Activity:');
      recent.slice(-5).forEach(req => {
        const time = new Date(req.timestamp).toLocaleTimeString();
        console.log(`  ${time} - ${req.cached ? '💾' : '🌐'} ${req.endpoint} [${req.service}]`);
      });
    }

    // Service-specific stats
    const services = ['RoomTypeService', 'DashboardService', 'RateService', 'HotelSettingsService'];
    console.log('\n🏢 Service Performance:');
    services.forEach(service => {
      const stats = this.getCacheHitRatio(service);
      if (stats.total > 0) {
        console.log(`  ${service}: ${stats.total} total, ${stats.hitRatio}% cached`);
      }
    });

    console.groupEnd();

    return {
      recent,
      overall,
      frequent,
      services: services.map(service => ({
        name: service,
        ...this.getCacheHitRatio(service)
      }))
    };
  }

  /**
   * Check for potential 429 risk patterns
   */
  checkFor429Risk() {
    const recent = this.getRecentRequests(2); // Last 2 minutes
    const uncachedCalls = recent.filter(req => !req.cached);
    
    // Risk factors
    const risks = [];
    
    if (uncachedCalls.length > 20) {
      risks.push(`High uncached request volume: ${uncachedCalls.length} in 2 minutes`);
    }

    // Check for rapid repeated calls to same endpoint
    const endpointCounts = new Map();
    uncachedCalls.forEach(req => {
      const key = req.endpoint;
      endpointCounts.set(key, (endpointCounts.get(key) || 0) + 1);
    });

    endpointCounts.forEach((count, endpoint) => {
      if (count > 5) {
        risks.push(`Repeated calls to ${endpoint}: ${count} times in 2 minutes`);
      }
    });

    if (risks.length > 0) {
      console.warn('⚠️ 429 Error Risk Detected:');
      risks.forEach(risk => console.warn(`  - ${risk}`));
    }

    return risks;
  }

  /**
   * Enable/disable monitoring
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log(`📊 API Monitor ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Clear all data
   */
  clear() {
    this.requests = [];
    this.cacheStats.clear();
    console.log('📊 API Monitor data cleared');
  }
}

// Create singleton instance
const apiMonitor = new ApiMonitor();

// Make apiMonitor globally accessible in development for debugging
if (process.env.NODE_ENV === 'development') {
  window.apiMonitor = apiMonitor;
  console.log('📊 API Monitor available globally as window.apiMonitor');
  console.log('Usage: apiMonitor.generateReport(), apiMonitor.checkFor429Risk()');
  
  // Auto-generate reports every 5 minutes in development
  setInterval(() => {
    if (apiMonitor.requests.length > 0) {
      apiMonitor.generateReport();
      apiMonitor.checkFor429Risk();
    }
  }, 5 * 60 * 1000);
}

export default apiMonitor;